import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, FormRecord } from '@angular/forms';
import { Geometry } from 'geojson';
import { firstValueFrom, Subscription } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AttributeValues } from '@connect-services/client/components/entity-attributes-input/entity-attributes-input.component';
import { AttributeValue } from '@connect-services/client/components/table-entity-detail/abstract-entity-detail.component';
import {
    EditEventAction,
    EntityProperties as EditEventEntityProperties,
} from '@connect-services/client/models/edit-event.model';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { AttributeInformationService } from '@connect-services/client/services/attribute-information.service';
import { AttributeValidationService } from '@connect-services/client/services/attribute-validation.service';
import { EditModeInputEventFacadeService } from '@connect-services/client/services/edit-mode-input-event-facade.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { ExtraObjectService } from '@connect-services/client/services/edition/extra-object.service';
import { EntityDetailCreationContextService } from '@connect-services/client/services/entity-detail-creation-context.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { readOnlyAttributes } from '@connect-services/client/services/layer.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import {
    ColumnsService,
    DepositWithFolderAndStandard,
    EditAction,
    ExtraObject,
    InformationSchemaTableExtended,
    Layer,
    LayerControllerGetRequestParams,
    LayersService,
    NonEditableColumns,
    PrecomputeResult,
} from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class EditEntityDetailService {
    public after = new Map<string, unknown>();
    public attributes: AttributeValue[] = [];
    public before = new Map<string, unknown>();
    public readonly bookmarkedAttributesForm: WritableSignal<FormRecord<FormControl<unknown>> | undefined> =
        signal(undefined);
    public readonly businessIdentifierKey = signal<string | undefined>(undefined);
    public readonly canEditBusinessIdentifier = signal(false);
    public readonly entityId = signal('');
    public readonly hasDirtyAttributes = signal(false);
    public readonly informationSchemaTableList = signal<InformationSchemaTableExtended[]>([]);
    public readonly isEditMode = signal<boolean>(false);
    public lastDataAttributesValue: EditEventEntityProperties = {};
    public readonly layer = signal<Layer | null>(null);
    public readonly notBookmarkedAttributesForm: WritableSignal<FormRecord<FormControl<unknown>> | undefined> =
        signal(undefined);
    public readonly recomputeError = signal<string | null>(null);

    private formEventsSubscription?: Subscription;
    private readonly layers = signal<Layer[]>([]);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly attributeInformationService = inject(AttributeInformationService);
    private readonly attributeValidationService = inject(AttributeValidationService);
    private readonly columnsService = inject(ColumnsService);
    private readonly editModeInputEventFacadeService = inject(EditModeInputEventFacadeService);
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);
    private readonly extraObjectService = inject(ExtraObjectService);
    private readonly fetchAllService = inject(FetchAllService);
    private readonly layersService = inject(LayersService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);

    public constructor() {
        effect(() => {
            if (this.isEditMode()) {
                return;
            }

            this.recomputeError.set(null);
        });
        effect(async () => {
            const projectId = this.projectBloc.value()?.id;
            const isEditMode = this.isEditMode();

            if (!projectId || !isEditMode) {
                return;
            }

            const layers: Layer[] = await this.fetchAllService.getAll<LayerControllerGetRequestParams, Layer>(
                this.layersService.layerControllerGet.bind(this.layersService),
                {
                    geomFilter: 'ALL',
                    project: projectId,
                },
            );

            this.layers.set(layers);
        });
    }

    public areFormsOnError(): boolean {
        return (
            Object.values(this.bookmarkedAttributesForm()?.controls ?? {}).some(
                (value: FormControl<unknown>) => value.errors,
            ) ||
            Object.values(this.notBookmarkedAttributesForm()?.controls ?? {}).some(
                (value: FormControl<unknown>) => value.errors,
            )
        );
    }

    public findCurrentInformationSchemaTable(attributeKey: string): InformationSchemaTableExtended | undefined {
        return this.informationSchemaTableList().find((information) => information.name === attributeKey);
    }

    public getAttributeFormControl(attributeKey: string): FormControl<unknown> | null {
        const bookmarkedControl = this.bookmarkedAttributesForm()?.get(attributeKey);
        if (bookmarkedControl instanceof FormControl) {
            return bookmarkedControl;
        }

        const notBookmarkedControl = this.notBookmarkedAttributesForm()?.get(attributeKey);
        if (notBookmarkedControl instanceof FormControl) {
            return notBookmarkedControl;
        }

        return null;
    }

    public async getAttributesValue(
        attributeKey: string,
        layer: Layer,
        page: number = 0,
        searchValue?: string,
    ): Promise<AttributeValues> {
        if (!layer.deposit) {
            return { errorKey: null, hasNextPage: false, values: [] };
        }

        const editSessionId = this.projectEditModeService.getEditSession()?.id;
        if (!editSessionId) {
            return { errorKey: null, hasNextPage: false, values: [] };
        }

        try {
            const attributeValues = await firstValueFrom(
                this.layersService.layerControllerGetStandardizedAttributesValues({
                    attributeColumn: attributeKey,
                    editSessionId,
                    id: layer.id,
                    page,
                    search: searchValue,
                    take: 20,
                }),
            );

            return {
                errorKey: attributeValues.errorKey,
                hasNextPage: attributeValues.labels?.hasNextPage ?? false,
                values: attributeValues.labels?.items ?? [],
            };
        } catch {
            return { errorKey: null, hasNextPage: false, values: [] };
        }
    }

    public getColumnNamesWithReferences(deposit: DepositWithFolderAndStandard, tableName: string): Promise<string[]> {
        if (!deposit.standard) {
            return new Promise((resolve) => resolve([]));
        }

        try {
            return firstValueFrom(
                this.columnsService.columnControllerGetColumnNamesWithReferences({
                    standard: deposit.standard.id,
                    tableName: tableName,
                }),
            );
        } catch {
            return new Promise((resolve) => resolve([]));
        }
    }

    public getCurrentGeom(dataEntity: EntityProperties | undefined): Geometry | null {
        const projectId = this.projectBloc.value()?.id;

        if (!projectId || !dataEntity) {
            return null;
        }

        AssertService.isString(dataEntity.uuid);
        const entityState = this.editModeStateService.getEntityState(projectId, dataEntity.layerId, dataEntity.uuid);

        return entityState?.properties?.['geom'] || dataEntity.geom || null;
    }

    public getFormErrors(formControl: FormControl<unknown>): string[] {
        return this.attributeValidationService.getErrorMessages(formControl);
    }

    public getNonEditableColumns(
        deposit: DepositWithFolderAndStandard,
        tableName: string,
    ): Promise<NonEditableColumns[]> {
        if (!deposit.standard) {
            return new Promise((resolve) => resolve([]));
        }

        try {
            return firstValueFrom(
                this.columnsService.columnControllerGetNonEditableColumns({
                    standard: deposit.standard.id,
                    tableName: tableName,
                }),
            );
        } catch {
            return new Promise((resolve) => resolve([]));
        }
    }

    public async initEditForm(
        bookmarkedAttributes: AttributeValue[],
        notBookmarkedAttributes: AttributeValue[],
        informationList: InformationSchemaTableExtended[] = [],
    ): Promise<void> {
        if (!this.projectBloc.value()?.id || !this.layer()?.id) {
            return;
        }

        const [bookmarkedEntities, notBookmarkedEntities] = this.initAttributeValues(
            bookmarkedAttributes,
            notBookmarkedAttributes,
        );

        this.informationSchemaTableList.set(
            informationList.length
                ? informationList
                : await this.attributeInformationService.loadForLayer(this.layer()!.id),
        );

        this.bookmarkedAttributesForm.set(this.initFormWithValidators(bookmarkedEntities));
        this.notBookmarkedAttributesForm.set(this.initFormWithValidators(notBookmarkedEntities));
        this.watchFormChanges();

        this.lastDataAttributesValue = {
            ...this.bookmarkedAttributesForm()!.value,
            ...this.notBookmarkedAttributesForm()!.value,
        };
        this.recomputeError.set(null);
    }

    public isValueEdited(attributeKey: string, value: unknown): boolean {
        const initial = this.getInitialValueForAttribute(attributeKey)?.value;

        if (value instanceof Date && typeof initial === 'string') {
            const initialDate = new Date(initial);
            if (Number.isNaN(initialDate.getTime())) {
                return true;
            }

            return initialDate.getTime() !== value.getTime();
        }

        if (value instanceof Date && initial instanceof Date) {
            return value.getTime() !== initial.getTime();
        }

        return value !== initial;
    }

    public markAttributeFormsAsPristine(): void {
        this.bookmarkedAttributesForm()?.markAsPristine();
        this.notBookmarkedAttributesForm()?.markAsPristine();
        this.refreshDirtyAttributesState();
    }

    public async recomputeEntityProperties(
        geometry: Geometry | null,
        action: EditAction,
        formEditActionEvent: EditEventAction | null,
        isStandardizeDeposit: boolean,
        shouldSave: boolean = false,
        sourceAttributes: Record<string, unknown> | null = null,
        sourceTableName: string | null = null,
    ): Promise<boolean> {
        if (!this.isEditMode()) {
            return true;
        }

        const project = this.projectBloc.value();
        const layer = this.layer();
        if (!project?.id || !layer?.id) {
            return false;
        }

        const attributes: { [key: string]: unknown } = {
            ...this.bookmarkedAttributesForm()?.value,
            ...this.notBookmarkedAttributesForm()?.value,
            geom: geometry,
        };

        const entityId: string | undefined = action === EditAction.UPDATE ? this.entityId() : undefined;
        if (this.entityId()) {
            attributes['uuid'] = this.entityId();
        }

        const editSessionId = this.projectEditModeService.getEditSession()?.id;

        if (!editSessionId) {
            return false;
        }

        if (!isStandardizeDeposit) {
            try {
                this.saveNonStandardizedEvent(layer, action, attributes, project.id);

                return true;
            } catch {
                return false;
            }
        }

        const recomputeResult: PrecomputeResult = await firstValueFrom(
            this.layersService.layerControllerPostEntityPrecompute({
                entityPrecomputeDto: {
                    action,
                    attributes,
                    editSessionId,
                    entityId,
                    sourceAttributes,
                    sourceTableName: sourceTableName ?? undefined,
                },
                layerId: layer.id,
            }),
        );

        if (!recomputeResult) {
            return false;
        }
        this.recomputeError.set(recomputeResult.success ? null : recomputeResult.message);

        if (!recomputeResult.success || !recomputeResult.attributes) {
            return recomputeResult.success;
        }

        this.bookmarkedAttributesForm.update(this.updateFormAttributeWithRecomputeResult(recomputeResult));
        this.notBookmarkedAttributesForm.update(this.updateFormAttributeWithRecomputeResult(recomputeResult));
        this.watchFormChanges();

        for (const attributesKey in recomputeResult.attributes) {
            this.after.set(attributesKey, recomputeResult.attributes[attributesKey]);
        }

        if (shouldSave) {
            const bulkUpdateEvent = this.setLayerStateAndCreateBulkUpdateEvent();
            const appEditActions = [formEditActionEvent, bulkUpdateEvent].filter(
                (action): action is EditEventAction => !!action,
            );
            this.saveExtraObjects(recomputeResult.extraObjects, appEditActions, project.id, recomputeResult.saveOrder);
        }

        return true;
    }

    public refreshDirtyAttributesState(): void {
        this.hasDirtyAttributes.set(
            !!this.bookmarkedAttributesForm()?.dirty || !!this.notBookmarkedAttributesForm()?.dirty,
        );
    }

    public resetFormsValues(): void {
        const reset = (form: FormGroup | undefined): void => {
            if (!form) {
                return;
            }

            const emptyValues: Record<string, unknown> = {};

            for (const key of Object.keys(form.controls)) {
                emptyValues[key] = null;
            }

            form.reset(emptyValues);
        };

        reset(this.bookmarkedAttributesForm());
        reset(this.notBookmarkedAttributesForm());

        this.after.clear();
        this.before.clear();
        this.refreshDirtyAttributesState();
    }

    public resetRecomputeError(): void {
        this.recomputeError.set(null);
    }

    public saveExtraObjects(
        extraObjects: ExtraObject[],
        formEventActions: EditEventAction[],
        projectId: string,
        saveOrder?: number,
    ): void {
        const extraObjectsLayers = this.extraObjectService.getExtraObjectsLayers(extraObjects, this.layers());

        const layerIds: string[] = Array.from(extraObjectsLayers.values()).map((layer: Layer) => layer.id);
        const projectState = this.editModeStateService.getProjectState(layerIds);

        const editActions = this.extraObjectService.getExtraObjectsEditActions(
            extraObjects,
            extraObjectsLayers,
            projectState,
        );

        const allActions: EditEventAction[] = [
            ...editActions,
            ...formEventActions.map((action: EditEventAction) => ({
                ...action,
                saveOrder: action.saveOrder ?? saveOrder,
            })),
        ];

        if (!allActions.length) {
            return;
        }

        this.editModeStateService.addEdit(projectId, { actions: allActions, saveOrder });
    }

    public setBusinessIdentifierInfo(): void {
        this.businessIdentifierKey.set(this.layer()?.businessIdentifier ?? undefined);
        this.canEditBusinessIdentifier.set(
            !!this.layer()?.businessIdentifier && this.layer()?.businessIdentifier !== 'uuid',
        );
    }

    public setLayerStateAndCreateBulkUpdateEvent(): EditEventAction | undefined {
        if (
            !this.layer()?.tableName ||
            !this.entityId() ||
            !this.projectBloc.value() ||
            !this.bookmarkedAttributesForm() ||
            !this.notBookmarkedAttributesForm()
        ) {
            return;
        }

        [...this.after.keys()]
            .filter((key) => !this.before.has(key))
            .forEach((key) => {
                this.before.set(key, this.lastDataAttributesValue?.[key]);
            });

        const businessIdentifier = this.attributes.find((attribute) => attribute.key === this.businessIdentifierKey());
        const projectId = this.projectBloc.value()!.id;
        const layerId = this.layer()!.id;
        const entityId = this.entityId()!;

        const beforeObject: Partial<EntityProperties> = Object.fromEntries(this.before.entries());
        const afterObject: Partial<EntityProperties> = Object.fromEntries(this.after.entries());

        this.after.forEach((value, key) => {
            this.before.set(key, value);
        });
        debugger;

        return this.editModeInputEventFacadeService.getBulkUpdateEvent({
            after: afterObject,
            before: beforeObject,
            businessIdentifier: {
                key: businessIdentifier?.key ?? 'uuid',
                value: businessIdentifier?.value ?? this.entityId(),
            },
            entityId,
            layerId,
            projectId,
            tableName: this.layer()!.tableName!,
        });
    }

    public setPendingGeometryChange(beforeGeometry: Geometry, afterGeometry: Geometry): void {
        if (!this.before.has('geom')) {
            this.before.set('geom', beforeGeometry);
        }

        this.after.set('geom', afterGeometry);
    }

    private getInitialValueForAttribute(attributeKey: string): AttributeValue | undefined {
        return this.attributes.find((attribute: AttributeValue): boolean => attribute.key === attributeKey);
    }

    private initAttributeValues(
        bookmarkedAttributes: AttributeValue[],
        notBookmarkedAttributes: AttributeValue[],
    ): [AttributeValue[], AttributeValue[]] {
        this.after.clear();
        this.before.clear();

        this.attributes = [...bookmarkedAttributes, ...notBookmarkedAttributes];

        if (!this.projectBloc.value()!.id || !this.layer() || !this.entityId()) {
            return [bookmarkedAttributes, notBookmarkedAttributes];
        }

        const entityState = this.editModeStateService.getEntityState(
            this.projectBloc.value()!.id,
            this.layer()!.id,
            this.entityId(),
        );

        if (
            !entityState ||
            this.entityDetailCreationContextService.isMapCreation() ||
            this.entityDetailCreationContextService.isTabularCreation() ||
            entityState.action !== EditAction.UPDATE
        ) {
            return [bookmarkedAttributes, notBookmarkedAttributes];
        }

        const bookmarkedEntities = this.mergeAttributeValueAndStateValue(bookmarkedAttributes, entityState.properties);

        const notBookmarkedEntities = this.mergeAttributeValueAndStateValue(
            notBookmarkedAttributes,
            entityState.properties,
        );

        const properties = Object.entries(entityState.properties ?? {});

        this.after = new Map(properties);
        this.before = new Map(properties);

        return [bookmarkedEntities, notBookmarkedEntities];
    }

    private initFormWithValidators(attributes: AttributeValue[]): FormRecord<FormControl<unknown | null>> {
        const form: FormRecord<FormControl<unknown | null>> = new FormRecord({});

        for (const attribute of attributes) {
            if (readOnlyAttributes.includes(attribute.key)) {
                form.addControl(attribute.key, new FormControl(attribute.value));

                continue;
            }

            const informationSchemaTable = this.informationSchemaTableList().find(
                (information) => information.name === attribute.key,
            );

            if (!informationSchemaTable) {
                continue;
            }

            const control = this.attributeValidationService.createControl(attribute.value, informationSchemaTable);

            form.addControl(attribute.key, control);
        }

        return form;
    }

    private mergeAttributeValueAndStateValue(
        attributes: AttributeValue[],
        properties: EditEventEntityProperties,
    ): AttributeValue[] {
        return attributes.map((attribute: AttributeValue) => {
            if (!attribute) {
                return attributes[0];
            }

            const attributeKey = attribute.key;
            if (properties && attributeKey in properties) {
                return { ...attribute, value: properties[attributeKey] };
            }

            return attribute;
        });
    }

    private saveNonStandardizedEvent(
        layer: Layer,
        action: EditAction,
        attributes: Record<string, unknown>,
        projectId: string,
    ): void {
        AssertService.isString(layer.tableName);
        const businessIdentifierKey = this.businessIdentifierKey() ?? 'uuid';
        const eventAction: EditEventAction = {
            action: action,
            after: attributes,
            before: this.before,
            businessIdentifier: { key: businessIdentifierKey, value: attributes[businessIdentifierKey] },
            entityId: this.entityId(),
            layerId: layer.id,
            tableName: layer.tableName,
        };
        this.editModeStateService.addEdit(projectId, { actions: [eventAction] });
    }

    private updateFormAttributeWithRecomputeResult(recomputeResult: PrecomputeResult) {
        return (
            attributeForm: FormRecord<FormControl<unknown>> | undefined,
        ): FormRecord<FormControl<unknown>> | undefined => {
            if (!attributeForm) {
                return attributeForm;
            }

            Object.entries(attributeForm.controls).forEach(([key, formControl]: [string, FormControl<unknown>]) => {
                if (
                    !Object.hasOwn(recomputeResult.attributes, key) ||
                    recomputeResult.attributes?.[key] === formControl.value
                ) {
                    return;
                }

                formControl.setValue(recomputeResult.attributes![key]);
                formControl.markAsDirty();
            });

            return new FormRecord(attributeForm.controls);
        };
    }

    private watchFormChanges(): void {
        this.formEventsSubscription?.unsubscribe();

        const bookmarkedForm = this.bookmarkedAttributesForm();
        const notBookmarkedForm = this.notBookmarkedAttributesForm();
        this.refreshDirtyAttributesState();

        if (!bookmarkedForm || !notBookmarkedForm) {
            return;
        }

        const updateDirtyState = (): void => {
            this.hasDirtyAttributes.set(bookmarkedForm.dirty || notBookmarkedForm.dirty);
        };

        this.formEventsSubscription = new Subscription();
        this.formEventsSubscription.add(bookmarkedForm.events.subscribe(updateDirtyState));
        this.formEventsSubscription.add(notBookmarkedForm.events.subscribe(updateDirtyState));
    }
}
